'use client'

import React, { createContext, useContext, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'

type Language = 'en' | 'ar'

const LanguageContext = createContext<{
  language: Language
  setLanguage: (lang: Language) => void
}>({
  language: 'en',
  setLanguage: () => {},
})

export const useLanguage = () => useContext(LanguageContext)

export const LanguageProvider: React.FC<{ children: React.ReactNode; initialLocale?: 'en' | 'ar' }> = ({
  children,
  initialLocale = 'en',
}) => {
  const [language, setLanguageState] = useState<Language>(initialLocale)
  const router = useRouter()
  const pathname = usePathname()

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)

    // set cookie so middleware + server components can read it
    document.cookie = `payload-locale=${lang}; path=/; max-age=31536000`

    // change URL prefix
    const segments = pathname.split('/')
    if (segments[1] === 'en' || segments[1] === 'ar') {
      segments[1] = lang // replace existing lang
    } else {
      segments.splice(1, 0, lang) // add lang if missing
    }

    const newPath = segments.join('/') || '/'
    router.push(newPath)
  }

  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>
}
